import {
    FC,
    ReactElement,
    useRef,
    useState,
} from 'react';

import { CheckIntersection, Wrapper } from '../../../components';
import { ConnectedBlockRenderer, ConnectedProductCard } from '../../../connectors';
import { Product } from '../../../entities/@products/Product/Product';
import { ProductDefault } from '../../../entities/@products/ProductDefault/ProductDefault';
import { ProductVariant } from '../../../entities/@products/ProductVariant/ProductVariant';
import { Brand } from '../../../entities/Brand/Brand';
import { Device } from '../../../entities/Device/Device';
import { Link } from '../../../entities/Link/Link';
import { AppRoute } from '../../../entities/Routing/Routing';
import { useDeviceWidth, useHasPassedElementOnScroll, useTrans } from '../../../hooks';
import { Breadcrumbs } from '../../Breadcrumbs/Breadcrumbs';
import { SliderSection } from '../../SliderSection/SliderSection';
import {
    ProductBrand,
    ProductDetail,
    ProductDetailHelmet,
    ProductInformation,
} from './subcomponents';

import './ProductDetailRenderer.scss';

interface ProductDetailRendererProps {
    productDefaultIsLoading: boolean;
    cartIsLoading: boolean;
    wishlistIsLoading: boolean;
    isInWishlist: boolean;
    breadcrumbs: Link[];
    productDefault?: ProductDefault;
    productVariant?: ProductVariant;
    brand?: Brand;
    brandProducts: Product[];
    relatedProducts: Product[];
    stockQuantity: number;
    inCartQuantity: number;
    onAddToCart: () => void;
    onWishlistButtonClick: () => void;
    className?: string;
}

export const ProductDetailRenderer: FC<ProductDetailRendererProps> = ({
    productDefaultIsLoading,
    cartIsLoading,
    wishlistIsLoading,
    isInWishlist,
    breadcrumbs,
    productDefault,
    productVariant,
    inCartQuantity,
    brand,
    brandProducts,
    relatedProducts,
    stockQuantity,
    onAddToCart,
    onWishlistButtonClick,
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const { isMobile } = useDeviceWidth();

    const callToActionsRef = useRef<HTMLDivElement>(null);

    const hasScrolledPassedCallToActions = useHasPassedElementOnScroll(callToActionsRef);

    const [hasScrolledPassedFooter, setHasScrolledPassedFooter] = useState<boolean>(false);

    const shouldShowFloatingCallToActionBar = !hasScrolledPassedFooter && (isMobile || hasScrolledPassedCallToActions);

    const productSliderColumns = {
        [Device.mobile]: 2.1,
        [Device.tabletLandscape]: 4,
    };

    return (
        <div className={`product-detail-renderer ${className}`}>
            {(productDefault && productVariant) && (
                <ProductDetailHelmet
                    productDefault={productDefault}
                    productVariant={productVariant}
                />
            )}

            <Wrapper>
                <Breadcrumbs
                    isLoading={productDefaultIsLoading}
                    breadcrumbs={breadcrumbs}
                    className="product-detail-renderer__breadcrumbs"
                />
            </Wrapper>

            <Wrapper className="product-detail-renderer__detail-wrapper">
                <ProductDetail
                    productDefaultIsLoading={productDefaultIsLoading}
                    cartIsLoading={cartIsLoading}
                    wishlistIsLoading={wishlistIsLoading}
                    hasScrolledPassedCallToActionsButtons={shouldShowFloatingCallToActionBar}
                    isInWishlist={isInWishlist}
                    productDefault={productDefault}
                    productVariant={productVariant}
                    inCartQuantity={inCartQuantity}
                    stockQuantity={stockQuantity}
                    onAddToCart={onAddToCart}
                    onWishlistButtonClick={onWishlistButtonClick}
                    callToActionsRef={callToActionsRef}
                />
            </Wrapper>

            {(isMobile && productVariant) && (
                <Wrapper className="product-detail-renderer__information-wrapper">
                    <ProductInformation productVariant={productVariant} />
                </Wrapper>
            )}

            {brand && (
                <ProductBrand
                    brand={brand}
                    products={brandProducts}
                    columns={productSliderColumns}
                    className="product-detail-renderer__brand-section"
                />
            )}

            {relatedProducts.length > 0 && (
                <SliderSection
                    titleHasThemeColor
                    id="related-products"
                    title={trans('containers.productDetailRenderer.relatedProducts')}
                    items={relatedProducts.map(relatedProduct => (
                        <ConnectedProductCard
                            key={relatedProduct.id}
                            product={relatedProduct}
                            className="product-detail-renderer__related-products-product"
                            pictureWrapperClassName="product-detail-renderer__related-products-picture-wrapper"
                        />
                    ))}
                    columns={productSliderColumns}
                    className="product-detail-renderer__related-products-slider"
                />
            )}

            <ConnectedBlockRenderer
                appRoute={AppRoute.productDetail}
                className="product-detail-renderer__block-renderer"
            />

            <CheckIntersection
                threshold={0.1}
                onIntersectionToggle={setHasScrolledPassedFooter}
            >
                {() => (
                    <div className="footer-observer" />
                )}
            </CheckIntersection>
        </div>
    );
};
